/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/api.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
/** API client. Transport self-heals (retry with exponential backoff + jitter); writes never auto-retry. */
import { isPublicDemo, currentUserId } from './users';

export class ApiError extends Error {
  constructor(message: string, public status: number, public errors?: string[], public auth?: string) { super(message); }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Public demo only: the picked person (lib/users.ts). With sign-in the session cookie says who acts. */
const actor = (): Record<string, string> => (isPublicDemo() ? { 'x-kontor-user': currentUserId() } : {});
/** The session ended (expired, signed out elsewhere, second factor missing): the sign-in gate takes over. */
const authLost = (status: number, data: unknown) => {
  if (status === 401 || (status === 403 && (data as { auth?: string } | null)?.auth === 'enrol')) window.dispatchEvent(new CustomEvent('kontor-auth'));
};

async function request<T>(method: string, url: string, body?: unknown, attempt = 0): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: { ...(body instanceof FormData || body === undefined ? {} : { 'content-type': 'application/json' }), ...actor() },
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if (method === 'GET' && attempt < 4) { await sleep(Math.min(4000, 250 * 2 ** attempt) + Math.random() * 200); return request(method, url, body, attempt + 1); }
    throw new ApiError('Keine Verbindung zum Server', 0);
  }
  if (res.status >= 500 && method === 'GET' && attempt < 3) { await sleep(300 * 2 ** attempt + Math.random() * 200); return request(method, url, body, attempt + 1); }
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('json') ? await res.json() : await res.text();
  if (!res.ok) {
    const auth = typeof data === 'object' && data && 'auth' in data ? String((data as { auth?: unknown }).auth ?? '') : undefined;
    if (res.status === 403 && auth === 'step_up') window.dispatchEvent(new CustomEvent('kontor-step-up'));
    authLost(res.status, data);
    throw new ApiError((data as any)?.error ?? res.statusText, res.status, (data as any)?.errors, auth || undefined);
  }
  return data as T;
}

export const api = {
  authHeaders: (): Record<string, string> => actor(),
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  del: <T>(url: string) => request<T>('DELETE', url),
  upload: <T>(url: string, files: File[], extra: Record<string, string> = {}) => {
    const fd = new FormData();
    files.forEach((f, i) => fd.append(`file${i}`, f, f.name));
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return request<T>('POST', url, fd);
  },
};

export type AgentEvent =
  | { type: 'thread'; threadId: string }
  | { type: 'step'; id: string; agent: string; label: string; detail?: string; state: 'running' | 'done' | 'error' }
  | { type: 'text'; delta: string }
  | { type: 'card'; card: any }
  | { type: 'done'; messageId: string }
  | { type: 'error'; message: string; retryable: boolean };

/** POST + Server-Sent Events reader for the agent endpoint. */
export async function streamChat(tid: string, body: { threadId?: string | null; text: string; attachments: string[]; surface?: 'agent' | 'command' | 'enforcement'; context?: unknown }, onEvent: (e: AgentEvent) => void, signal?: AbortSignal) {
  const res = await fetch(`/api/t/${tid}/chat`, { method: 'POST', headers: { 'content-type': 'application/json', ...actor() }, body: JSON.stringify(body), signal });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => null);
    const auth = typeof data === 'object' && data && 'auth' in data ? String((data as { auth?: unknown }).auth ?? '') : undefined;
    if (res.status === 403 && auth === 'step_up') window.dispatchEvent(new CustomEvent('kontor-step-up'));
    authLost(res.status, data);
    throw new ApiError((data as any)?.error ?? `Agent nicht erreichbar (${res.status})`, res.status, (data as any)?.errors, auth || undefined);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = /^event: (.*)$/m.exec(chunk)?.[1];
      const data = chunk.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trimStart()).join('\n');
      if (!data) continue;
      const parsed = JSON.parse(data);
      onEvent(ev === 'thread' ? { type: 'thread', threadId: parsed.threadId } : parsed);
    }
  }
}
